import type {
  ChartSpec,
  NotebookChapterSpec,
  NotebookKpi,
  NotebookTemplate,
  TableSpec,
} from '../../types';
import { coerceWidgetSpecs } from './widgets/widgetTypes';

const TEMPLATES = new Set<NotebookTemplate>([
  'kpi_row',
  'trend',
  'mix',
  'ranking',
  'compare',
  'heatmap',
  'table_story',
  'multi_viz',
  'analysis',
]);

function firstChart(
  charts: ChartSpec[] | undefined,
  types?: ChartSpec['type'][],
): ChartSpec | undefined {
  if (!charts?.length) return undefined;
  if (!types) return charts[0];
  return charts.find(c => types.includes(c.type)) ?? charts[0];
}

function firstTable(tables: TableSpec[] | undefined): TableSpec | undefined {
  return tables?.[0];
}

function kpisOf(spec: NotebookChapterSpec, max = 16): NotebookKpi[] {
  return (spec.kpis ?? []).slice(0, max);
}

/** Honest empty lists (rowCount === 0) still paint; dropped/broken tables do not. */
export function tablePaints(table: TableSpec): boolean {
  if ((table.data?.length ?? 0) > 0) return true;
  return (table.columns?.length ?? 0) > 0 && table.rowCount === 0;
}

export function chapterHasVisuals(spec: NotebookChapterSpec): boolean {
  return (
    (spec.kpis?.length ?? 0) > 0 ||
    (spec.charts?.some(chart => (chart.data?.length ?? 0) > 0) ?? false) ||
    (spec.tables?.some(tablePaints) ?? false) ||
    (spec.widgets?.length ?? 0) > 0
  );
}

/** Force a model chapter into the slots of its declared template. Keep KPIs as a fallback. */
export function coerceChapter(spec: NotebookChapterSpec): NotebookChapterSpec {
  const template = TEMPLATES.has(spec.template) ? spec.template : 'kpi_row';
  const charts = spec.charts?.filter(c => (c.data?.length ?? 0) > 0) ?? [];
  const tables = spec.tables?.filter(tablePaints) ?? [];
  const kpis = kpisOf(spec);
  const widgets = coerceWidgetSpecs(spec.widgets);

  let next: NotebookChapterSpec;
  switch (template) {
    case 'kpi_row':
      next = widgets.length
        ? { ...spec, template: 'multi_viz', kpis, charts: [], tables: [], widgets }
        : { ...spec, template, kpis, charts: [], tables: [], widgets };
      break;
    case 'trend': {
      if (charts.length >= 2) {
        next = {
          ...spec,
          template: 'multi_viz',
          kpis,
          charts: charts.slice(0, 4),
          tables: tables.slice(0, 2),
          widgets,
        };
        break;
      }
      const chart = firstChart(charts, ['compareLine', 'compareArea', 'line', 'area']);
      next = { ...spec, template, kpis, charts: chart ? [chart] : [], tables: [], widgets };
      break;
    }
    case 'mix': {
      if (charts.length >= 2) {
        next = {
          ...spec,
          template: 'multi_viz',
          kpis,
          charts: charts.slice(0, 4),
          tables: tables.slice(0, 2),
          widgets,
        };
        break;
      }
      const chart = firstChart(charts, ['pie', 'stackedBar', 'horizontalBar', 'bar']);
      next = { ...spec, template, kpis, charts: chart ? [chart] : [], tables: [], widgets };
      break;
    }
    case 'ranking': {
      const chart = firstChart(charts, ['horizontalBar', 'bar', 'compareBar']);
      const table = firstTable(tables);
      next = {
        ...spec,
        template,
        kpis,
        charts: chart ? [chart] : [],
        tables: !chart && table ? [table] : [],
        widgets,
      };
      break;
    }
    case 'compare': {
      const preferred = charts.filter(c =>
        c.type === 'compareBar' ||
        c.type === 'compareLine' ||
        c.type === 'compareArea' ||
        c.type === 'combo',
      );
      const picked = (preferred.length ? preferred : charts).slice(0, 2);
      const table = firstTable(tables);
      next = {
        ...spec,
        template: table && !picked.length ? 'table_story' : template,
        kpis: table ? [] : kpis,
        charts: picked,
        tables: table ? [table] : [],
        widgets,
      };
      break;
    }
    case 'heatmap': {
      const chart = firstChart(charts, ['heatmap']);
      next = { ...spec, template, kpis, charts: chart ? [chart] : [], tables: [], widgets };
      break;
    }
    case 'table_story': {
      const table = firstTable(tables);
      next = {
        ...spec,
        template,
        kpis: [],
        charts: [],
        tables: table ? [table] : [],
        widgets,
      };
      break;
    }
    case 'multi_viz': {
      next = {
        ...spec,
        template,
        kpis,
        charts: charts.slice(0, 4),
        tables: tables.slice(0, 2),
        widgets,
      };
      break;
    }
    case 'analysis': {
      const chart = firstChart(charts, [
        'bar',
        'horizontalBar',
        'compareBar',
        'compareLine',
        'heatmap',
      ]);
      const table = firstTable(tables);
      next = {
        ...spec,
        template,
        kpis: table || chart ? [] : kpis,
        charts: chart ? [chart] : [],
        tables: table ? [table] : [],
        widgets: [],
      };
      break;
    }
  }

  if (chapterHasVisuals(next)) return next;
  if (widgets.length) {
    return { ...spec, template: 'multi_viz', kpis, charts: [], tables: [], widgets };
  }
  if (charts.length || tables.length) {
    return {
      ...spec,
      template: 'multi_viz',
      kpis,
      charts: charts.slice(0, 4),
      tables: tables.slice(0, 2),
      widgets,
    };
  }
  if (kpis.length) {
    return { ...spec, template: 'kpi_row', kpis, charts: [], tables: [], widgets };
  }
  return next;
}
